/**
 * LeadAddressBlock.jsx — Practice address fields for the Contact Info tab.
 *
 * Section §1A: single address entry with Street / City / State / ZIP +
 *              optional location name.
 * Section §1B: multi-location support via "+ Add location" (max 5).
 *              Primary is index 0 — its City, State drives the lead
 *              header sub-line + Leads list practice column.
 * Section §1C: ContactAddressInline — collapsed/expanded inline address
 *              for each additional contact card (single address only).
 *
 * Storage: addresses[] = [{id, name?, street, city, state, zip}]
 */
import React, { useState, useMemo } from "react";

const US_STATES = [
  ["AL","Alabama"],["AK","Alaska"],["AZ","Arizona"],["AR","Arkansas"],
  ["CA","California"],["CO","Colorado"],["CT","Connecticut"],["DE","Delaware"],
  ["DC","District of Columbia"],["FL","Florida"],["GA","Georgia"],["HI","Hawaii"],
  ["ID","Idaho"],["IL","Illinois"],["IN","Indiana"],["IA","Iowa"],
  ["KS","Kansas"],["KY","Kentucky"],["LA","Louisiana"],["ME","Maine"],
  ["MD","Maryland"],["MA","Massachusetts"],["MI","Michigan"],["MN","Minnesota"],
  ["MS","Mississippi"],["MO","Missouri"],["MT","Montana"],["NE","Nebraska"],
  ["NV","Nevada"],["NH","New Hampshire"],["NJ","New Jersey"],["NM","New Mexico"],
  ["NY","New York"],["NC","North Carolina"],["ND","North Dakota"],["OH","Ohio"],
  ["OK","Oklahoma"],["OR","Oregon"],["PA","Pennsylvania"],["RI","Rhode Island"],
  ["SC","South Carolina"],["SD","South Dakota"],["TN","Tennessee"],["TX","Texas"],
  ["UT","Utah"],["VT","Vermont"],["VA","Virginia"],["WA","Washington"],
  ["WV","West Virginia"],["WI","Wisconsin"],["WY","Wyoming"],
];

const labelTiny = { fontSize: 10, textTransform: "uppercase", color: "#888", letterSpacing: "0.3px", fontWeight: 500, marginBottom: 3, display: "block" };
const inputBase = { width: "100%", border: "0.5px solid #dddddd", borderRadius: 8, padding: "7px 10px", fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" };

function FieldInput({ label, value, onChange, placeholder, type = "text", testid, flex }) {
  return (
    <div style={flex ? { flex } : {}}>
      <span style={labelTiny}>{label}</span>
      <input
        data-testid={testid}
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        onFocus={(e) => (e.currentTarget.style.borderColor = "#2563eb")}
        onBlur={(e) => (e.currentTarget.style.borderColor = "#dddddd")}
        style={inputBase}
      />
    </div>
  );
}

function StateSelect({ value, onChange, testid, flex }) {
  return (
    <div style={flex ? { flex } : {}}>
      <span style={labelTiny}>State</span>
      <select
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onFocus={(e) => (e.currentTarget.style.borderColor = "#2563eb")}
        onBlur={(e) => (e.currentTarget.style.borderColor = "#dddddd")}
        style={inputBase}
      >
        <option value="">State</option>
        {US_STATES.map(([code, name]) => (
          <option key={code} value={code}>{code} — {name}</option>
        ))}
      </select>
    </div>
  );
}

function newAddress() {
  return { id: `addr-${Math.random().toString(36).slice(2, 8)}`, name: "", street: "", city: "", state: "", zip: "" };
}

function normalizeAddresses(lead) {
  const arr = Array.isArray(lead.addresses) ? [...lead.addresses] : [];
  if (arr.length === 0) {
    // Migrate legacy scalar fields onto a single address entry if any present.
    const has = lead.address || lead.city || lead.state || lead.zip;
    if (has) {
      arr.push({
        id: "addr-primary",
        name: "",
        street: lead.address || "",
        city: lead.city || "",
        state: lead.state || "",
        zip: lead.zip || "",
      });
    }
  }
  return arr.map((a) => ({
    id: a.id || `addr-${Math.random().toString(36).slice(2, 8)}`,
    name: a.name || "",
    street: a.street || a.address || "",
    city: a.city || "",
    state: a.state || "",
    zip: a.zip || "",
  }));
}

/**
 * Single address container used by both the lead-level multi-location
 * block and the per-contact inline editor.
 */
function AddressContainer({ entry, index, total, onChange, onRemove, allowRemove, idxLabel, testidPrefix = "addr" }) {
  const [confirmRemove, setConfirmRemove] = useState(false);
  const update = (key, val) => onChange({ ...entry, [key]: val });

  return (
    <div
      data-testid={`${testidPrefix}-row-${index}`}
      style={{
        background: "#f9f9f9",
        border: "0.5px solid #eeeeee",
        borderRadius: 8,
        padding: "12px 14px",
        marginBottom: 8,
        position: "relative",
      }}
    >
      {idxLabel && (
        <div style={{ fontSize: 12, fontWeight: 500, color: "#222", marginBottom: 8 }}>
          {(entry.name && entry.name.trim()) ? entry.name : `Location ${index + 1}`}
        </div>
      )}
      {allowRemove && !confirmRemove && (
        <button
          data-testid={`${testidPrefix}-remove-${index}`}
          onClick={() => setConfirmRemove(true)}
          style={{ position: "absolute", top: 8, right: 10, background: "none", border: "none", color: "#A32D2D", fontSize: 14, cursor: "pointer", lineHeight: 1 }}
          aria-label="Remove address"
        >×</button>
      )}
      {confirmRemove && (
        <div style={{ position: "absolute", top: 6, right: 10, fontSize: 11, color: "#666", display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span>Remove this address?</span>
          <button
            data-testid={`${testidPrefix}-remove-confirm-${index}`}
            onClick={() => { setConfirmRemove(false); onRemove?.(); }}
            style={{ background: "none", border: "none", color: "#A32D2D", cursor: "pointer", fontSize: 11, fontWeight: 600 }}
          >Yes</button>
          <button
            data-testid={`${testidPrefix}-remove-cancel-${index}`}
            onClick={() => setConfirmRemove(false)}
            style={{ background: "none", border: "none", color: "#185FA5", cursor: "pointer", fontSize: 11 }}
          >Cancel</button>
        </div>
      )}

      {/* Row 1: Street (full width) */}
      <div style={{ marginBottom: 8 }}>
        <FieldInput
          label="Street address"
          value={entry.street}
          onChange={(v) => update("street", v)}
          placeholder="123 Main Street, Suite 400"
          testid={`${testidPrefix}-street-${index}`}
        />
      </div>

      {/* Row 2: City / State / ZIP (3-col flex 2/1/1) */}
      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        <FieldInput
          label="City"
          value={entry.city}
          onChange={(v) => update("city", v)}
          placeholder="Chicago"
          testid={`${testidPrefix}-city-${index}`}
          flex={2}
        />
        <StateSelect
          value={entry.state}
          onChange={(v) => update("state", v)}
          testid={`${testidPrefix}-state-${index}`}
          flex={1}
        />
        <FieldInput
          label="ZIP code"
          value={entry.zip}
          onChange={(v) => update("zip", v)}
          placeholder="60601"
          testid={`${testidPrefix}-zip-${index}`}
          flex={1}
        />
      </div>

      {/* Row 3: Location name (full width, optional) */}
      <div>
        <FieldInput
          label="Location name (optional)"
          value={entry.name}
          onChange={(v) => update("name", v)}
          placeholder="e.g. Main Office, Satellite Clinic, Downtown Location"
          testid={`${testidPrefix}-name-${index}`}
        />
        <div style={{ fontSize: 10, color: "#aaaaaa", marginTop: 4 }}>
          Add a label to distinguish multiple locations.
        </div>
      </div>
    </div>
  );
}

/**
 * Lead-level multi-location practice address block.
 * Renders below Time Zone in the Contact & Practice Identity section.
 */
export function LeadPracticeAddressBlock({ lead, onSave }) {
  const initial = useMemo(() => normalizeAddresses(lead), [lead]);
  const [addresses, setAddresses] = useState(initial.length > 0 ? initial : [newAddress()]);

  React.useEffect(() => {
    const fresh = normalizeAddresses(lead);
    if (fresh.length === 0) {
      setAddresses([newAddress()]);
    } else {
      setAddresses(fresh);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead.id]);

  const persist = (next) => {
    setAddresses(next);
    // Strip empty addresses on save
    const cleaned = next.filter((a) => a.street || a.city || a.state || a.zip || a.name);
    onSave?.(cleaned);
  };

  const updateAt = (idx, entry) => persist(addresses.map((a, i) => (i === idx ? entry : a)));
  const removeAt = (idx) => persist(addresses.filter((_, i) => i !== idx));
  const addLocation = () => {
    if (addresses.length >= 5) return;
    persist([...addresses, newAddress()]);
  };

  const showMulti = addresses.length > 1;

  return (
    <div data-testid="lead-practice-addresses" style={{ gridColumn: "1 / -1" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontSize: 11, textTransform: "uppercase", color: "#888", letterSpacing: "0.4px" }}>Practice address</span>
        {addresses.length < 5 && (
          <button
            data-testid="lead-add-location-btn"
            onClick={addLocation}
            style={{ background: "none", border: "none", color: "#2563eb", fontSize: 11, cursor: "pointer", padding: 0 }}
          >+ Add location</button>
        )}
      </div>

      {addresses.map((entry, idx) => (
        <AddressContainer
          key={entry.id}
          entry={entry}
          index={idx}
          total={addresses.length}
          onChange={(updated) => updateAt(idx, updated)}
          onRemove={() => removeAt(idx)}
          allowRemove={addresses.length > 1 || (addresses.length === 1 && (entry.street || entry.city || entry.state || entry.zip || entry.name))}
          idxLabel={showMulti}
          testidPrefix="lead-addr"
        />
      ))}
    </div>
  );
}

/**
 * Inline single-address editor for additional contact cards (§1C).
 * Renders collapsed by default — "Address: …" or "+ Add address"
 * — and expands into the same 4-field layout when clicked.
 */
export function ContactAddressInline({ address, onChange }) {
  const safe = address && typeof address === "object" ? address : null;
  const hasAddr = !!(safe && (safe.street || safe.city || safe.state || safe.zip));
  const [expanded, setExpanded] = useState(false);

  if (!expanded) {
    return (
      <div data-testid="contact-address-collapsed" style={{ marginTop: 6 }}>
        {hasAddr ? (
          <button
            data-testid="contact-address-expand"
            onClick={() => setExpanded(true)}
            style={{ background: "none", border: "none", color: "#666", fontSize: 12, cursor: "pointer", padding: 0 }}
          >
            Address: {[safe.street, safe.city, safe.state].filter(Boolean).join(", ")} ↓
          </button>
        ) : (
          <button
            data-testid="contact-address-add"
            onClick={() => setExpanded(true)}
            style={{ background: "none", border: "none", color: "#2563eb", fontSize: 11, cursor: "pointer", padding: 0 }}
          >+ Add address</button>
        )}
      </div>
    );
  }

  const entry = safe || { id: `caddr-${Math.random().toString(36).slice(2, 8)}`, name: "", street: "", city: "", state: "", zip: "" };
  const update = (next) => onChange?.(next);

  return (
    <div data-testid="contact-address-expanded" style={{ marginTop: 6 }}>
      <AddressContainer
        entry={entry}
        index={0}
        total={1}
        onChange={(updated) => update(updated)}
        onRemove={() => update(null)}
        allowRemove={hasAddr}
        idxLabel={false}
        testidPrefix="contact-addr"
      />
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: -4 }}>
        <button
          data-testid="contact-address-collapse"
          onClick={() => setExpanded(false)}
          style={{ background: "none", border: "none", color: "#185FA5", fontSize: 11, cursor: "pointer", padding: 0 }}
        >▲ Collapse</button>
      </div>
    </div>
  );
}

export { US_STATES };
